"use client";

import { CaretDown, Play, VideoCamera } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

type WatchNavMenuProps = {
  isDark: boolean;
  label: string;
  liveLabel: string;
  liveSublabel: string;
  wordOfWeekLabel: string;
  wordOfWeekSublabel: string;
  locale: string;
};

export function WatchNavMenu({
  isDark,
  label,
  liveLabel,
  liveSublabel,
  wordOfWeekLabel,
  wordOfWeekSublabel,
  locale,
}: WatchNavMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          "flex items-center gap-2 rounded-full px-3 py-2 text-[10px] font-bold uppercase tracking-widest transition-all xl:px-6 xl:text-[11px]",
          isDark
            ? "text-white/80 hover:bg-white/10 hover:text-white"
            : "text-black hover:bg-black/5 hover:text-black/70"
        )}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {label}
        <CaretDown
          className={cn("h-3.5 w-3.5 transition-transform duration-200", open && "rotate-180")}
          weight="bold"
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="absolute left-0 top-full z-30 pt-3"
          >
            <div
              className={cn(
                "w-[16.5rem] overflow-hidden rounded-2xl p-1.5 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.12)]",
                "border border-black/[0.04] bg-white/90 backdrop-blur-xl",
                "dark:border-white/[0.06] dark:bg-zinc-900/90 dark:shadow-[0_24px_48px_-12px_rgba(0,0,0,0.4)]",
                /* Liquid-glass inner refraction */
                "shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]"
              )}
            >
              <Link
                href={`/${locale}/watch`}
                className="group flex items-center gap-3.5 rounded-xl px-3.5 py-3 transition-colors hover:bg-zinc-950/[0.04] dark:hover:bg-white/[0.06]"
                onClick={() => setOpen(false)}
              >
                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-brand-red/8 text-brand-red transition-colors group-hover:bg-brand-red group-hover:text-white dark:bg-brand-red/15">
                  <Play size={14} weight="fill" />
                </span>
                <div className="min-w-0">
                  <span className="block text-[13px] font-bold text-zinc-950 dark:text-white">
                    {liveLabel}
                  </span>
                  <span className="block text-[11px] leading-snug text-zinc-400 dark:text-zinc-500">
                    {liveSublabel}
                  </span>
                </div>
              </Link>

              <Link
                href={`/${locale}/word-of-the-week`}
                className="group flex items-center gap-3.5 rounded-xl px-3.5 py-3 transition-colors hover:bg-zinc-950/[0.04] dark:hover:bg-white/[0.06]"
                onClick={() => setOpen(false)}
              >
                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-brand-gold/10 text-brand-gold transition-colors group-hover:bg-brand-gold group-hover:text-white dark:bg-brand-gold/15">
                  <VideoCamera size={14} weight="fill" />
                </span>
                <div className="min-w-0">
                  <span className="block text-[13px] font-bold text-zinc-950 dark:text-white">
                    {wordOfWeekLabel}
                  </span>
                  <span className="block text-[11px] leading-snug text-zinc-400 dark:text-zinc-500">
                    {wordOfWeekSublabel}
                  </span>
                </div>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
